import { toast } from 'react-toastify';
import { UserRole } from '@/api/apis/user';
import { CaseClinicalSpecialistResource, OpoContactWithPrimary } from '@/api/apis/case';
import { employeeLabelWeight } from '@/constants/resources.constants';
import { RoleKeyedPartial } from '@/types';

export type AssignDetachState<T = string> = {
  assign: T[];
  detach: T[];
};
export type AssignDetachUsersState = RoleKeyedPartial<AssignDetachState<string>>;

export const mergeUsersByRoleAssignDetach = (external: RoleKeyedPartial<string[]>, pending: AssignDetachUsersState) => {
  const merged: RoleKeyedPartial<string[]> = {};
  const allRoles = Array.from(new Set([...Object.keys(external), ...Object.keys(pending)]));
  allRoles.forEach((role) => {
    const extIds = external[role] || [];
    const pend = pending[role] || { assign: [], detach: [] };
    merged[role] = Array.from(new Set([...extIds, ...pend.assign])).filter((id) => !pend.detach.includes(id));
  });
  return merged;
};

export const mergeAssignDetachWithExternalUsers = (
  pending: AssignDetachUsersState,
  external: RoleKeyedPartial<string[]>,
): AssignDetachUsersState => {
  const merged: AssignDetachUsersState = { ...pending };
  Object.keys(external).forEach((role) => {
    const extIds = external[role] || [];
    const pend = pending[role] || { assign: [], detach: [] };
    merged[role] = {
      assign: pend.assign.filter((id) => !extIds.includes(id)),
      detach: pend.detach.filter((id) => extIds.includes(id)),
    };
  });
  Object.keys(merged).forEach((role) => {
    if (!external[role] && !pending[role]) {
      delete merged[role];
    }
  });
  return merged;
};

export const mergeAssignDetachWithExternalIds = (pending: AssignDetachState, external: string[]): AssignDetachState => {
  return {
    assign: pending.assign.filter((id) => !external.includes(id)),
    detach: pending.detach.filter((id) => external.includes(id)),
  };
};

export const mergeAssignDetachWithExternalContacts = (
  pending: AssignDetachState<OpoContactWithPrimary>,
  external: OpoContactWithPrimary[],
): AssignDetachState<OpoContactWithPrimary> => {
  const extIds = external.map((c) => c.opoContactId);
  return {
    assign: pending.assign.filter((c) => !extIds.includes(c.opoContactId)),
    detach: pending.detach.filter((c) => extIds.includes(c.opoContactId)),
  };
};

export function updateAssignDetachIdsByRole(
  pending: AssignDetachUsersState,
  id: string,
  role: UserRole,
  external?: RoleKeyedPartial<string[]>,
): AssignDetachUsersState {
  const prev = pending[role] || { assign: [], detach: [] };
  const extIds: string[] = external?.[role] || [];
  if (extIds.includes(id)) {
    return {
      ...pending,
      [role]: {
        assign: prev.assign,
        detach: prev.detach.includes(id) ? prev.detach.filter((x) => x !== id) : [...prev.detach, id],
      },
    };
  }
  return {
    ...pending,
    [role]: {
      assign: prev.assign.includes(id) ? prev.assign.filter((x) => x !== id) : [...prev.assign, id],
      detach: prev.detach,
    },
  };
}

export function updateAssignDetachIdsFlat(
  pending: AssignDetachState,
  id: string,
  external?: string[],
): AssignDetachState {
  const prev = pending;
  const extIds: string[] = Array.isArray(external) ? external : [];
  if (extIds.includes(id)) {
    return {
      assign: prev.assign,
      detach: prev.detach.includes(id) ? prev.detach.filter((x) => x !== id) : [...prev.detach, id],
    };
  }
  return {
    assign: prev.assign.includes(id) ? prev.assign.filter((x) => x !== id) : [...prev.assign, id],
    detach: prev.detach,
  };
}

export function updateAssignDetachOpoContacts(
  pending: AssignDetachState<OpoContactWithPrimary>,
  id: string,
  external?: OpoContactWithPrimary[],
) {
  const prev = pending;
  const extIds: string[] = Array.isArray(external) ? external.map((e) => e.opoContactId) : [];
  if (extIds.includes(id)) {
    return {
      assign: prev.assign,
      detach: prev.detach.find((c) => c.opoContactId === id)
        ? prev.detach.filter((x) => x.opoContactId !== id)
        : [...prev.detach, { opoContactId: id, isPrimary: false }],
    };
  }
  if (prev.assign.some((c) => c.opoContactId === id)) {
    return {
      assign: prev.assign.filter((c) => c.opoContactId !== id),
      detach: prev.detach,
    };
  }
  return {
    assign: [...prev.assign, { opoContactId: id, isPrimary: false }],
    detach: prev.detach,
  };
}

export function showRoleDetachWarning(
  role: UserRole,
  usersByRole: RoleKeyedPartial<string[]>,
  roleNames: Record<UserRole, string>,
) {
  if ((usersByRole[role]?.length ?? 0) === 0) {
    toast.warning(`The last user with role ${roleNames[role]} is detached from the case.`);
  }
}

export function showOpoContactDetachWarning(contacts: OpoContactWithPrimary[]) {
  if (contacts.length === 0) {
    toast.warning('The last opo contact is detached from the case.');
  }
}

export const sortClinicalSpecialistsByEmployeeLabel = (csList: CaseClinicalSpecialistResource[]) => {
  return csList.toSorted((a, b) => {
    const leftLabelWeight = a.resourceData?.labels?.[0] ? employeeLabelWeight[a.resourceData?.labels?.[0]] : Infinity;
    const rightLabelWeight = b.resourceData?.labels?.[0] ? employeeLabelWeight[b.resourceData?.labels?.[0]] : Infinity;

    return leftLabelWeight - rightLabelWeight;
  });
};
